/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { memo } from 'react';

import { jsx } from '@compiled/react';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { RecommendationBannerProps } from './types';

/**
 * Generic, stateless recommendation banner used on the Rovo Space landing page.
 *
 * Designed to be the shared visual primitive behind multiple Spaces
 * recommendation surfaces. Known wrappers:
 *
 * - `PastConversationsBanner` ("Bring past conversations into this space")
 *   — ROVOFE-3042 (this ticket).
 * - "Bring your links into this space" — upcoming follow-up ticket; will
 *   import this component, swap the icon and copy, and supply its own
 *   `onPrimaryAction` handler. **Please keep this component free of
 *   feature-specific assumptions** (e.g. don't hard-code an icon, copy, or
 *   analytics event here) so additional wrappers stay trivial to add.
 *
 * Callers own visibility, dismiss persistence (if any), data-fetching and
 * analytics.
 */

const styles = cssMap({
	bannerWrapper: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.100'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
		marginBlockEnd: token('space.150'),
	},
	iconCell: {
		flexShrink: 0,
	},
	textBlock: {
		minWidth: 0,
	},
	actions: {
		flexShrink: 0,
	},
	// Stacked variant: the buttons stretch to the full inline width of the
	// banner card. `<Button>` does not expose a `fullWidth` prop, so we wrap
	// each action in a `<Box>` whose `display: block` + a width-100% style on
	// the button itself give the desired full-bleed appearance.
	stackedAction: {
		width: '100%',
	},
});

const RecommendationBannerComponent = ({
	icon,
	title,
	description,
	primaryActionLabel,
	onPrimaryAction,
	onDismiss,
	dismissLabel,
	variant = 'inline',
	testId = 'recommendation-banner',
}: RecommendationBannerProps): React.JSX.Element => {
	if (variant === 'stacked') {
		return (
			<Box xcss={styles.bannerWrapper} testId={testId}>
				<Stack space="space.150">
					<Flex alignItems="start" gap="space.150">
						<Box xcss={styles.iconCell} aria-hidden="true">
							{icon}
						</Box>
						<Box xcss={styles.textBlock}>
							<Stack space="space.025">
								<Text weight="bold" color="color.text">
									{title}
								</Text>
								<Text size="small" color="color.text.subtlest">
									{description}
								</Text>
							</Stack>
						</Box>
					</Flex>
					{/*
					 * Per the ROVOFE-3190 design (Slack-confirmed), the primary
					 * CTA sits on TOP and the subtle dismiss button sits BELOW
					 * in the stacked variant. This intentionally inverts the
					 * default ADS modal-footer order so the most-tappable
					 * (bottom) button is the safe, non-destructive secondary.
					 */}
					<Stack space="space.100">
						<Box xcss={styles.stackedAction}>
							<Button
								appearance="primary"
								onClick={onPrimaryAction}
								testId={`${testId}-primary-action`}
								shouldFitContainer
							>
								{primaryActionLabel}
							</Button>
						</Box>
						{onDismiss && (
							<Box xcss={styles.stackedAction}>
								{/*
								 * Per the ROVOFE-3190 Figma, the stacked
								 * Dismiss button has a faint border / card
								 * outline. ADS `Button` exposes that via the
								 * `default` appearance — `subtle` is text-only
								 * and would not match the design.
								 */}
								<Button
									appearance="default"
									onClick={onDismiss}
									testId={`${testId}-dismiss`}
									shouldFitContainer
								>
									{/*
									 * `dismissLabel` is required by the
									 * discriminated union when `onDismiss` is
									 * set, so it is guaranteed to be a
									 * translated string here. In the stacked
									 * variant it is rendered as the visible
									 * button text.
									 */}
									{dismissLabel as string}
								</Button>
							</Box>
						)}
					</Stack>
				</Stack>
			</Box>
		);
	}

	return (
		<Box xcss={styles.bannerWrapper} testId={testId}>
			<Flex alignItems="center" gap="space.150">
				<Box xcss={styles.iconCell} aria-hidden="true">
					{icon}
				</Box>
				<Box xcss={styles.textBlock}>
					<Stack space="space.025">
						<Text weight="bold" color="color.text">
							{title}
						</Text>
						<Text size="small" color="color.text.subtlest">
							{description}
						</Text>
					</Stack>
				</Box>
				<Inline xcss={styles.actions} space="space.100" alignBlock="center">
					<Button
						appearance="primary"
						onClick={onPrimaryAction}
						testId={`${testId}-primary-action`}
					>
						{primaryActionLabel}
					</Button>
					{onDismiss && (
						<IconButton
							appearance="subtle"
							icon={(iconProps) => (
								<CrossIcon {...iconProps} label="" color={token('color.icon.subtle')} />
							)}
							// `dismissLabel` is required by the discriminated union when
							// `onDismiss` is set, so it is guaranteed to be a translated
							// string here.
							label={dismissLabel as string}
							onClick={onDismiss}
							testId={`${testId}-dismiss`}
						/>
					)}
				</Inline>
			</Flex>
		</Box>
	);
};

export const RecommendationBanner: React.MemoExoticComponent<
	(props: RecommendationBannerProps) => React.JSX.Element
> = memo(RecommendationBannerComponent);
